import React, { useState, useEffect } from 'react';
import { SeqViz } from 'seqviz';
import seqparse from 'seqparse';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Paper,
  Box,
  Typography,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Draggable from 'react-draggable';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ViewStreamIcon from '@mui/icons-material/ViewStream';

// Draggable dialog paper component
function PaperComponent(props) {
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

export default function GenBankViewer({ open, onClose, genbankContent, fileName, entryNum, title }) {
  const [parsedSeq, setParsedSeq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewerType, setViewerType] = useState('linear'); // Default to linear view

  // Add global style and observer to override tooltip delays
  useEffect(() => {
    if (open) {
      // Add aggressive CSS overrides
      const style = document.createElement('style');
      style.textContent = `
        .la-vz-tooltip {
          transition: none !important;
          animation: none !important;
          opacity: 1 !important;
          visibility: visible !important;
        }
        .la-vz-annotation:hover + .la-vz-tooltip,
        .la-vz-annotation:hover ~ .la-vz-tooltip,
        *:hover > .la-vz-tooltip {
          display: block !important;
          opacity: 1 !important;
          visibility: visible !important;
          transition-delay: 0s !important;
        }
      `;
      document.head.appendChild(style);

      // Use MutationObserver to immediately show tooltips as they're created
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1 && node.classList && node.classList.contains('la-vz-tooltip')) {
              // Force immediate display
              node.style.transition = 'none';
              node.style.opacity = '1';
              node.style.visibility = 'visible';
              node.style.display = 'block';
            }
          });
        });
      });

      // Start observing after a short delay to ensure the viewer is mounted
      setTimeout(() => {
        const viewerElement = document.querySelector('.la-vz-viewer');
        if (viewerElement) {
          observer.observe(viewerElement, {
            childList: true,
            subtree: true
          });
        }
      }, 100);

      return () => {
        document.head.removeChild(style);
        observer.disconnect();
      };
    }
  }, [open]);

  useEffect(() => {
    if (genbankContent && open) {
      setLoading(true);
      setError(null);

      // Parse the GenBank content
      seqparse(genbankContent)
        .then((result) => {
          // Process annotations and add colors
          if (result.annotations) {
            result.annotations = result.annotations.map(ann => {
              // Assign colors based on feature type
              let { color } = ann;
              if (!color) {
                if (ann.type === 'CDS') color = '#FFB300';
                else if (ann.type === 'gene') color = '#2196F3';
                else if (ann.type === 'primer_bind') color = '#9C27B0';
                else if (ann.type === 'misc_feature') color = '#4CAF50';
                else if (ann.type === 'source') color = '#607D8B';
                else color = '#2CACE2';
              }
              // Ensure the annotation has a name for visibility
              const name = ann.name || ann.type || 'feature';
              return {
                ...ann,
                name,
                color
              };
            });
          }

          // Extract translations from CDS features
          const translations = [];
          if (result.annotations) {
            result.annotations.forEach(ann => {
              if (ann.type === 'CDS' && ann.direction) {
                // Create translation entry for CDS
                translations.push({
                  start: ann.start,
                  end: ann.end,
                  direction: ann.direction,
                  name: ann.name || 'CDS'
                });
              }
            });
          }

          setParsedSeq({
            ...result,
            translations: translations.length > 0 ? translations : result.translations
          });
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error parsing GenBank file:', err);
          setError(err.message || 'Failed to parse GenBank file');
          setLoading(false);
        });
    }
  }, [genbankContent, open]);

  const handleViewerTypeChange = (event, newViewerType) => {
    if (newViewerType !== null) {
      setViewerType(newViewerType);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperComponent={PaperComponent}
      maxWidth="lg"
      fullWidth
      aria-labelledby="draggable-dialog-title"
      PaperProps={{
        style: {
          minHeight: '700px',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle
        style={{ cursor: 'move', backgroundColor: '#f5f5f5' }}
        id="draggable-dialog-title"
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">
              {title || 'Sequence Viewer'}
            </Typography>
            {fileName && (
              <Typography variant="body2" color="textSecondary">
                Entry #{entryNum} - {fileName}
              </Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {parsedSeq && !loading && (
              <ToggleButtonGroup
                value={viewerType}
                exclusive
                onChange={handleViewerTypeChange}
                size="small"
                aria-label="viewer type"
              >
                <ToggleButton value="linear" aria-label="linear view">
                  <ViewStreamIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="circular" aria-label="circular view">
                  <RadioButtonUncheckedIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="both_flip" aria-label="both views">
                  <AccountTreeIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <Box sx={{ minHeight: '550px', width: '100%' }}>
          {loading && (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              minHeight="400px"
            >
              <CircularProgress />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Loading sequence data...
              </Typography>
            </Box>
          )}

          {error && (
            <Box p={2}>
              <Typography color="error">
                Error loading sequence: {error}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Please check if the GenBank file is valid.
              </Typography>
            </Box>
          )}

          {parsedSeq && !loading && !error && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                {parsedSeq.name && (
                  <Typography variant="subtitle2" color="textSecondary">
                    Sequence: {parsedSeq.name} ({parsedSeq.seq ? parsedSeq.seq.length : 0} bp)
                  </Typography>
                )}
                <Box textAlign="right">
                  <Typography variant="caption" color="textSecondary" display="block">
                    Tip: Use mouse wheel to zoom, click and drag to pan
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block">
                    Hover over short features to display annotation(s)
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  position: 'relative',
                  '& .la-vz-tooltip': {
                    transition: 'none !important',
                    animationDuration: '0s !important',
                    transitionDelay: '0s !important',
                  },
                  '& *': {
                    transition: 'opacity 0s !important',
                  },
                  '& .la-vz-annotation': {
                    cursor: 'pointer',
                  },
                  '& .la-vz-annotation:hover': {
                    opacity: 1,
                  }
                }}
                onMouseOver={(e) => {
                  // Try to force immediate tooltip display on hover
                  const target = e.target;
                  if (target && target.classList && target.classList.contains('la-vz-annotation')) {
                    const tooltip = target.querySelector('.la-vz-tooltip');
                    if (tooltip) {
                      tooltip.style.display = 'block';
                      tooltip.style.opacity = '1';
                      tooltip.style.visibility = 'visible';
                    }
                  }
                }}
              >
                <SeqViz
                  name={parsedSeq.name || fileName}
                  seq={parsedSeq.seq}
                  annotations={parsedSeq.annotations || []}
                  primers={parsedSeq.primers || []}
                  enzymes={parsedSeq.enzymes || []}
                  translations={parsedSeq.translations || []}
                  viewer={viewerType}
                  style={{
                    height: "550px",
                    width: "100%",
                  }}
                  showIndex
                  showComplement
                  showAnnotations
                  showPrimers
                  showCutSites
                  showTranslations
                  zoom={{ linear: 15, circular: 0 }}
                  bpColors={{
                    'A': '#FF6B6B',
                    'T': '#4ECDC4',
                    'G': '#45B7D1',
                    'C': '#96CEB4'
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}